
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const MinimalistTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={containerClass}>
       <div className="p-16 flex flex-col h-full">
           {!hideHeader && (
             <>
               {/* Minimalist Header Grid */}
               <div className="grid grid-cols-2 gap-8 border-b-2 border-neutral-900 pb-12 mb-12">
                   <div>
                       <h1 className="text-4xl font-black text-neutral-900 tracking-tighter uppercase mb-4">{profile.name}</h1>
                       <div className="text-sm font-medium text-neutral-600 space-y-1">
                           <p>{profile.address}</p>
                           <p>{profile.email}</p>
                           <p>{profile.phone}</p>
                       </div>
                   </div>
                   <div className="text-right flex flex-col items-end justify-between">
                       <h2 className="text-6xl font-thin text-neutral-200 uppercase tracking-tighter">{type}</h2>
                       <div className="text-right">
                           <p className="text-sm font-bold uppercase tracking-wider text-neutral-900 mb-1">{title}</p>
                           {metaData.find(m => m.label.includes('Ref') || m.label.includes('#')) && (
                               <p className="text-lg font-mono text-neutral-500">{metaData.find(m => m.label.includes('Ref') || m.label.includes('#'))?.value}</p>
                           )}
                       </div>
                   </div>
               </div>

               {/* Client & Meta Info */}
               <div className="grid grid-cols-4 gap-8 mb-16">
                   <div className="col-span-2">
                       <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-4">Prepared For</p>
                       {client ? (
                           <div>
                               <p className="font-bold text-xl text-neutral-900">{client.name}</p>
                               <p className="text-neutral-500">{client.company}</p>
                               <p className="text-sm text-neutral-400 mt-2">{client.address}</p>
                           </div>
                       ) : <span className="text-neutral-300">No Client</span>}
                   </div>
                   <div className="col-span-2 grid grid-cols-2 gap-8">
                       {metaData.filter(m => !m.label.includes('Ref') && !m.label.includes('#')).map((m, i) => (
                           <div key={i}>
                               <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">{m.label}</p>
                               <p className="font-medium text-neutral-900">{m.value}</p>
                           </div>
                       ))}
                   </div>
               </div>
             </>
           )}

           {/* Content */}
           <div className="flex-1 minimalist-table-container">
               {children}
           </div>

           {/* Minimal Footer */}
           <div className="mt-auto pt-8 flex justify-between items-end">
               <div className="max-w-md">
                   <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">Terms & Notes</p>
                   <p className="text-sm text-neutral-600 leading-relaxed">{footerNote}</p>
               </div>
               <LogoImage size="md" className="opacity-50 grayscale hover:grayscale-0 transition-all" />
           </div>
       </div>
    </div>
  );
};

export default MinimalistTemplate;
